import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  useEmployeeExamination,
  useUpdateEmployeeExamination,
} from "../api/employeeDocs";
import type { ExaminationStatus, ExaminationStatusDetail } from "../types.employee_docs";

interface Props {
  userId: string;
}

const STATUS_BADGE: Record<ExaminationStatus, { color: "green" | "yellow" | "red" | "gray"; label: string }> = {
  ok:      { color: "green",  label: "Ważne" },
  soon:    { color: "yellow", label: "Wkrótce wygasa" },
  expired: { color: "red",    label: "Wygasło" },
  missing: { color: "gray",   label: "Brak daty" },
};

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL");
}

function ExaminationCard({
  label,
  status,
  onEdit,
}: {
  label: string;
  status: ExaminationStatusDetail | null;
  onEdit: () => void;
}) {
  const s = status ?? { valid_until: null, status: "missing" as const, days_remaining: null };
  const badge = STATUS_BADGE[s.status];

  return (
    <div className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--color-text-primary)]">{label}</span>
        <Badge color={badge.color}>{badge.label}</Badge>
      </div>
      <div className="text-sm text-[var(--color-text-secondary)]">
        Ważne do:{" "}
        <span className="font-medium text-[var(--color-text-primary)]">
          {formatDate(s.valid_until)}
        </span>
      </div>
      {s.days_remaining !== null && s.status !== "missing" && (
        <div className="text-xs text-[var(--color-text-tertiary)]">
          {s.days_remaining >= 0
            ? `Pozostało ${s.days_remaining} dni`
            : `Minęło ${Math.abs(s.days_remaining)} dni`}
        </div>
      )}
      <Button variant="ghost" size="sm" onClick={onEdit} className="mt-1">
        Edytuj
      </Button>
    </div>
  );
}

const MIN_DATE = "2020-01-01";

function isDateValid(val: string) {
  if (!val) return true;
  return val >= MIN_DATE;
}

interface EditState {
  bhp_valid_until: string;
  medical_valid_until: string;
  notes: string;
}

export function EmployeeExaminationPanel({ userId }: Props) {
  const { data: exam, isLoading } = useEmployeeExamination(userId);
  const update = useUpdateEmployeeExamination(userId);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<EditState>({ bhp_valid_until: "", medical_valid_until: "", notes: "" });
  const [dateError, setDateError] = useState<string | null>(null);

  function openEdit() {
    setForm({
      bhp_valid_until:     exam?.bhp_valid_until     ?? "",
      medical_valid_until: exam?.medical_valid_until ?? "",
      notes:               exam?.notes               ?? "",
    });
    setDateError(null);
    setEditMode(true);
  }

  function handleSave() {
    if (!isDateValid(form.bhp_valid_until) || !isDateValid(form.medical_valid_until)) {
      setDateError("Data nie może być wcześniejsza niż 2020.");
      return;
    }
    setDateError(null);
    update.mutate(
      {
        bhp_valid_until:     form.bhp_valid_until     || null,
        medical_valid_until: form.medical_valid_until || null,
        notes:               form.notes,
      },
      { onSuccess: () => setEditMode(false) }
    );
  }

  if (isLoading) {
    return <div className="text-sm text-[var(--color-text-tertiary)] py-2">Ładowanie danych BHP…</div>;
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
        BHP i Medycyna pracy
      </h4>

      {!editMode ? (
        <div className="flex gap-3">
          <ExaminationCard
            label="Szkolenie BHP"
            status={exam?.bhp_status ?? null}
            onEdit={openEdit}
          />
          <ExaminationCard
            label="Medycyna pracy"
            status={exam?.medical_status ?? null}
            onEdit={openEdit}
          />
        </div>
      ) : (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs font-medium text-[var(--color-text-secondary)]">BHP ważne do</span>
              <DatePicker
                value={form.bhp_valid_until}
                min={MIN_DATE}
                onChange={(v) => setForm((f) => ({ ...f, bhp_valid_until: v }))}
                className="border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-[var(--color-text-secondary)]">Medycyna pracy ważna do</span>
              <DatePicker
                value={form.medical_valid_until}
                min={MIN_DATE}
                onChange={(v) => setForm((f) => ({ ...f, medical_valid_until: v }))}
                className="border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">Uwagi</span>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-none"
            />
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={update.isPending}>
              {update.isPending ? "Zapisywanie…" : "Zapisz"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>
              Anuluj
            </Button>
          </div>
          {dateError && <p className="text-xs text-red-600">{dateError}</p>}
          {update.isError && (
            <p className="text-xs text-red-600">Błąd zapisu. Spróbuj ponownie.</p>
          )}
        </div>
      )}
    </div>
  );
}
